import React from "react";
import styled from "styled-components/macro";
import { Badge, BottomNavigation as BottomNavigationM, BottomNavigationAction } from "@mui/material";
import { useViewerDecks, useViewerQuery } from "queries/viewerQuery";
import { useLibraryUrl, useMatchMain } from "root/RootRouter";
import { FIVEHINT, MANY_UNKNOWN } from "root/main/main/Main";
import { devicePaddings } from "tools/device";
import Pulse from "./Pulse";
import { FIRSTLOOPDONE } from "root/main/learn/Learn";
import { useIsOffline } from "tools/offline";
import { TUTORIALDONE } from "root/tutorial/steps/Verdict";
import FADuotoneIcon from "icons/FADuotoneIcon";
import { useTranslation } from "react-i18next";
import { MAIN, PROFILE, PRACTICE } from "../../root/RootRouter.config";
import { CONTENT_ID } from "components/header/constants";
import { useNotificationReengagement } from "hooks/useNotificationReengagement";

export const BOTTOMNAVID = "bottomNav";

const { bottom } = devicePaddings();
const SBottomNavigation = styled(BottomNavigationM)`
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.duo.color.grey};
  background: white;
  height: ${64 + bottom}px;
  padding: 0 16px;
  .Mui-disabled {
    color: ${({ theme }) => theme.duo.color.grey};
  }
  .Mui-selected {
    color: unset;
  }
  button {
    height: 64px;
    padding-bottom: 4px;
  }
`;

type Props = {
  onChange: (event: React.ChangeEvent<{}>, value: string) => void;
  value: string;
};

export const PRACTICE_BUTTON_ID = "practice-button";
export const LIBRARY_BUTTON_ID = "library-button";

const BottomNavigation: React.FC<Props> = (p) => {
  const { t } = useTranslation();
  const { hintWasSeen, viewer } = useViewerQuery();
  const { deck } = useViewerDecks();
  const pulsingLibrary = useMatchMain({ end: false }) && !deck?.stats.total && hintWasSeen(FIVEHINT);
  const libraryUrl = useLibraryUrl();
  const isOffline = useIsOffline();
  const shouldShowNotificationBadge = useNotificationReengagement();

  const hasSeenTutorial = hintWasSeen(TUTORIALDONE);
  const hasSeenHintsCombo = hintWasSeen(FIVEHINT) && hintWasSeen(FIRSTLOOPDONE);
  const emailNotVerified = !viewer?.emailVerified;

  const shouldShowAccountBadge =
    shouldShowNotificationBadge || (emailNotVerified && (hasSeenTutorial || hasSeenHintsCombo));

  return (
    <SBottomNavigation onChange={p.onChange} value={p.value} id={BOTTOMNAVID}>
      <BottomNavigationAction
        value={MAIN.url()}
        onClick={() => document.getElementById(CONTENT_ID)?.scrollTo({ top: 0, behavior: "smooth" })}
        icon={
          <Badge invisible={(deck?.stats.unknown || 0) < MANY_UNKNOWN} color="primary" variant="dot">
            <FADuotoneIcon rotate={20} size={27} label={t("LEARNING")} switchedColors icon="learningCardsBlank" />
          </Badge>
        }
      />

      <BottomNavigationAction
        value={PRACTICE.url()}
        disabled={isOffline}
        id={PRACTICE_BUTTON_ID}
        icon={<FADuotoneIcon switchedColors size={30} label={t("PRACTICE")} icon="messages" />}
      />

      <BottomNavigationAction
        value={libraryUrl}
        disabled={isOffline}
        id={LIBRARY_BUTTON_ID}
        icon={
          <Pulse iterations={pulsingLibrary ? "infinite" : 0} bold>
            <FADuotoneIcon switchedColors label={t("LIBRARY")} icon="libraryBooks" />
          </Pulse>
        }
      />

      <BottomNavigationAction
        value={PROFILE.url()}
        disabled={isOffline}
        icon={
          <Badge invisible={!shouldShowAccountBadge} variant="dot" color="error">
            <FADuotoneIcon label={t("ACCOUNT")} icon="accountUserVNeck" />
          </Badge>
        }
      />
    </SBottomNavigation>
  );
};

export default BottomNavigation;
